/* Bottom navigation.

   Labelled for their contents rather than with vague umbrellas — "Services",
   "Orders", "Account", not "More". Specificity is what makes a destination
   predictable before you tap it.

   Four tabs, not five. There used to be a "Saved" tab holding favourited
   shops, and it went when shops stopped being something the customer browses:
   a partner is assigned, or — for a drive-in wash — picked inside the booking
   itself. A favourites list is a discovery surface, and there is no discovery
   of partners left to do.

   The active indicator is a shared `layoutId` so it slides between tabs. The
   icons themselves do not animate: this bar is tapped dozens of times a
   session, and an animation at that frequency stops being feedback and starts
   being a delay.

   For the same reason the indicator uses `springSnap` (0.28s) rather than the
   `springMove` (0.4s) that repositioning elsewhere in the app uses. Frequency
   sets the budget: 400ms is imperceptible on a sheet opened twice a session
   and is a drag on a control tapped forty times. */

import { NavLink, useLocation } from 'react-router-dom'
import { motion, useReducedMotion } from 'motion/react'
import { CalendarCheck, House, LayoutGrid, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { springSnap } from '../../design/motion'
import { useCart } from '../../state/cart'

export const TABS = [
  { to: '/app', icon: House, key: 'home' },
  /* A grid, because that is literally what the screen is: every service we
     sell, laid out. The old `Compass` said "go wander", which was right for a
     shop directory and is wrong for a list of three things. */
  { to: '/explore', icon: LayoutGrid, key: 'services' },
  { to: '/orders', icon: CalendarCheck, key: 'orders' },
  { to: '/account', icon: User, key: 'account' },
] as const

export function TabBar() {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const { pathname } = useLocation()
  const { count } = useCart()

  /* Hidden wherever the bottom of the screen belongs to something else — a
     checkout's pay button, a full-screen search — and on the landing page,
     which is a marketing surface with its own nav and footer. Rendering it
     under those and relying on z-index leaves a dead 56px strip that swallows
     taps.

     `/basket` belongs on that list for exactly the reason the list exists: it
     draws the same `.basket-foot` pay bar the checkout does, at a higher
     z-index, which covered the tab bar completely and left five destinations
     that looked absent and behaved worse — invisible, but still eating every
     tap that landed on them. */
  const hidden = pathname === '/' || /^\/(search|basket|checkout|auth|order\/)/.test(pathname)
  if (hidden) return null

  return (
    /* Named for the landmark it is, not for the tab that happens to be first.
       This used to be `nav.home`, which a screen reader announced as "Home
       navigation" — a label that describes one of its children. */
    <nav className="tabbar glass" aria-label={t('a11y.primaryNav')}>
      <ul className="tabbar-list">
        {TABS.map(({ to, icon: TabIcon, key }) => (
          <li key={to}>
            <NavLink
              to={to}
              end
              className={({ isActive }) => `tab ${isActive ? 'is-active' : ''}`}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      className="tab-glow"
                      layoutId="tab-glow"
                      transition={reduce ? { duration: 0 } : springSnap}
                      aria-hidden
                    />
                  )}
                  <span className="tab-icon">
                    <TabIcon size={21} strokeWidth={isActive ? 2.3 : 1.9} />
                    {key === 'orders' && count > 0 && (
                      <span className="tab-dot" aria-hidden />
                    )}
                  </span>
                  <span className="tab-label">{t(`nav.${key}`)}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
